import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import axios from '../../config/axios';
import { getApiUrl } from '../../config/environment';

export interface Cid10Option {
  code: string;
  description: string;
  chapter?: string;
}

interface Cid10AutocompleteProps {
  /** Código atualmente selecionado (ex.: "H40"). */
  value: string;
  /** Descrição atual (para exibir o item já salvo mesmo sem busca). */
  description?: string;
  onChange: (code: string, description: string) => void;
  label?: string;
  required?: boolean;
  size?: 'small' | 'medium';
  disabled?: boolean;
}

/**
 * Campo de busca de CID-10 (sugestão médica 29.05): digite código ou termo
 * (ex.: "glaucoma") e selecione na lista. Busca no endpoint /clinic/cid10.
 */
const Cid10Autocomplete: React.FC<Cid10AutocompleteProps> = ({
  value,
  description,
  onChange,
  label = 'CID',
  required = false,
  size = 'small',
  disabled = false,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<Cid10Option[]>([]);
  const [loading, setLoading] = useState(false);

  const selected: Cid10Option | null = useMemo(
    () => (value ? { code: value, description: description || value } : null),
    [value, description],
  );

  useEffect(() => {
    const term = inputValue.trim();
    let active = true;
    const handle = setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await axios.get(getApiUrl('/clinic/cid10'), { params: term ? { q: term } : {} });
        if (active) setOptions(resp.data?.data || []);
      } catch {
        if (active) setOptions([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [inputValue]);

  return (
    <Autocomplete<Cid10Option, false, false, true>
      freeSolo
      size={size}
      disabled={disabled}
      options={options}
      loading={loading}
      value={selected}
      filterOptions={(x) => x}
      isOptionEqualToValue={(opt, val) =>
        (typeof opt === 'string' ? opt : opt.code) === (typeof val === 'string' ? val : val.code)
      }
      getOptionLabel={(opt) =>
        typeof opt === 'string' ? opt : opt.code ? `${opt.code} — ${opt.description}` : ''
      }
      onInputChange={(_, v) => setInputValue(v)}
      onChange={(_, val) => {
        if (!val) {
          onChange('', '');
        } else if (typeof val === 'string') {
          onChange(val.toUpperCase(), '');
        } else {
          onChange(val.code, val.description);
        }
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={required ? `${label} *` : label}
          required={required}
          placeholder="Digite código ou termo (ex.: glaucoma)"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default Cid10Autocomplete;
